import { memo, useCallback, useEffect, useState } from "react";
import { BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, Button } from "@src/components";
import { BottomSheetFormInput } from "@src/components/FormInput/BottomSheetFormInput";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import type { SiteUser } from "@src/features/settings/data/mock-sites";
import { AppBottomSheet } from "@src/features/common/components/AppBottomSheet";

interface EditUserBottomSheetProps {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  savedUser: SiteUser;
  onSave: (data: { email: string }) => void;
}

export const EditUserBottomSheet = memo(({ bottomSheetRef, savedUser, onSave }: EditUserBottomSheetProps) => {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();

  const [email, setEmail] = useState(savedUser.email);
  const [errors, setErrors] = useState<{ email?: string }>({});

  // Resync when the edited user switches without unmount — replaces the
  // previous key-based remount pattern, which reset all local state.
  // Keyed on id (identity), not email, so two different users sharing an
  // email still trigger the reset.
  useEffect(() => {
    setEmail(savedUser.email);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: reset on user identity, not on email edits
  }, [savedUser.id]);

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    if (value.trim()) setErrors((prev) => (prev.email ? { ...prev, email: undefined } : prev));
  }, []);

  const handleDismiss = useCallback(() => {
    setEmail(savedUser.email);
    setErrors({});
  }, [savedUser.email]);

  const handleSave = useCallback(() => {
    const newErrors: { email?: string } = {};
    if (!email.trim()) newErrors.email = "Enter email address";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({ email });
    setErrors({});
    setTimeout(() => {
      bottomSheetRef.current?.dismiss();
    }, 100);
  }, [email, onSave, bottomSheetRef]);

  return (
    <AppBottomSheet
      bottomSheetRef={bottomSheetRef}
      enableDynamicSizing
      enablePanDownToClose
      keyboardBehavior="interactive"
      keyboardBlurBehavior="restore"
      android_keyboardInputMode="adjustResize"
      backgroundStyle={{ backgroundColor: theme.colors.surfaceCard }}
      onDismiss={handleDismiss}
    >
      <BottomSheetScrollView
        style={styles.container}
        contentContainerStyle={{ paddingBottom: bottom + theme.spacing["5xl"] }}
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
      >
        <Text textStyle="lg/semibold" style={styles.title}>
          Edit user
        </Text>

        <View gap="3xl">
          <BottomSheetFormInput
            placeholder="Email address"
            value={email}
            onChangeText={handleEmailChange}
            size="medium"
            keyboardType="email-address"
            autoCapitalize="none"
            error={errors.email}
          />
        </View>

        <Button variant="primary" label="Save" size="md" onPress={handleSave} style={styles.saveButton} />
      </BottomSheetScrollView>
    </AppBottomSheet>
  );
});

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing["5xl"],
  },
  title: {
    marginBottom: theme.spacing["4xl"],
  },
  saveButton: {
    marginTop: theme.spacing["4xl"],
  },
}));
